import type { ComponentProps, ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { Button as ButtonPrimitive } from '../primitives/button.tsx';
import { Spinner } from './Spinner.tsx';
import { Tooltip } from './Tooltip.tsx';

export interface IconButtonProps extends Omit<ComponentProps<'button'>, 'children'> {
  /** Required: becomes the aria-label and the tooltip. */
  label: string;
  icon: ReactNode;
  variant?: 'ghost' | 'secondary' | 'danger';
  size?: 'xs' | 'sm' | 'md';
  shortcut?: string[];
  shortcutThen?: boolean;
  tooltipSide?: 'top' | 'right' | 'bottom' | 'left';
  /** Skip the tooltip (only when the same words are visible right beside it). */
  noTooltip?: boolean;
  loading?: boolean;
  /** Toggle buttons: pressed state. */
  pressed?: boolean;
}

const SIZE = { xs: 'icon-xs', sm: 'icon-sm', md: 'icon' } as const;

/** Icon colours step up on hover: subtle at rest so a toolbar of icons stays quiet. */
const VARIANT = {
  ghost: { variant: 'ghost', className: 'text-fg-subtle' },
  secondary: { variant: 'secondary', className: 'text-fg-muted hover:text-fg' },
  danger: { variant: 'ghost', className: 'text-fg-subtle hover:bg-destructive-soft hover:text-destructive-ink active:bg-destructive-soft' },
} as const;

/** An icon-only button on the shadcn Button. Always has a tooltip and an aria-label (a11y/icon-button-label). */
export function IconButton({
  label,
  icon,
  variant = 'ghost',
  size = 'sm',
  shortcut,
  shortcutThen,
  tooltipSide,
  noTooltip,
  loading,
  pressed,
  disabled,
  className,
  type = 'button',
  ...rest
}: IconButtonProps) {
  const v = VARIANT[variant];
  return (
    <Tooltip content={label} shortcut={shortcut} shortcutThen={shortcutThen} side={tooltipSide} enabled={!noTooltip}>
      <ButtonPrimitive
        type={type}
        variant={v.variant}
        size={SIZE[size]}
        aria-label={label}
        aria-pressed={pressed}
        aria-busy={loading || undefined}
        disabled={disabled || loading}
        className={cn(v.className, pressed && 'bg-fg/10 text-fg hover:bg-fg/10', className)}
        {...rest}
      >
        {loading ? <Spinner size={size === 'md' ? 16 : 14} /> : icon}
      </ButtonPrimitive>
    </Tooltip>
  );
}
